import { supabase } from '../lib/supabase';
import { convertToCSV, parseCSV, downloadCSV, readCSVFile } from '../utils/fileUtils';

/**
 * Column definitions for each entity type
 */
const COLUMN_DEFINITIONS = {
  musicals: [
    { key: 'id', header: 'ID', type: 'text', editable: false },
    { key: 'title', header: 'Title', type: 'text', required: true },
    { key: 'composer', header: 'Composer', type: 'text' },
    { key: 'lyricist', header: 'Lyricist', type: 'text' },
    { key: 'book_writer', header: 'Book Writer', type: 'text' },
    { key: 'year_premiered', header: 'Year Premiered', type: 'integer' },
    { key: 'genre', header: 'Genre', type: 'text' },
    { key: 'description', header: 'Description', type: 'text' },
    { key: 'published_by', header: 'Published By', type: 'text' },
    { key: 'rights_group', header: 'Rights Group', type: 'text' },
    { key: 'poster_url', header: 'Poster URL', type: 'text' },
    { key: 'is_active', header: 'Is Active', type: 'boolean', default: true }
  ],
  
  songs: [
    { key: 'id', header: 'ID', type: 'text', editable: false },
    { key: 'title', header: 'Title', type: 'text', required: true },
    { key: 'musical_id', header: 'Musical ID', type: 'text', required: true },
    { key: 'act_number', header: 'Act Number', type: 'integer' },
    { key: 'song_order', header: 'Song Order', type: 'integer' },
    { key: 'key_signature', header: 'Key Signature', type: 'text' },
    { key: 'is_featured', header: 'Is Featured', type: 'boolean', default: false },
    { key: 'audio_url', header: 'Audio URL', type: 'text' },
    { key: 'midi_file_path', header: 'MIDI File Path', type: 'text' },
    { key: 'mp3_file_path', header: 'MP3 File Path', type: 'text' }
  ],
  
  roles: [
    { key: 'id', header: 'ID', type: 'text', editable: false },
    { key: 'character_name', header: 'Character Name', type: 'text', required: true },
    { key: 'musical_id', header: 'Musical ID', type: 'text', required: true },
    { key: 'role_type', header: 'Role Type', type: 'text' },
    { key: 'voice_type', header: 'Voice Type', type: 'text' },
    { key: 'vocal_range', header: 'Vocal Range', type: 'text' },
    { key: 'age_range', header: 'Age Range', type: 'text' },
    { key: 'description', header: 'Description', type: 'text' },
    { key: 'is_principal', header: 'Is Principal', type: 'boolean', default: false },
    { key: 'assigned_talent_id', header: 'Assigned Talent ID', type: 'text' }
  ],
  
  talent_profiles: [
    { key: 'id', header: 'ID', type: 'text', editable: false },
    { key: 'first_name', header: 'First Name', type: 'text', required: true },
    { key: 'last_name', header: 'Last Name', type: 'text', required: true },
    { key: 'display_name', header: 'Display Name', type: 'text' },
    { key: 'email', header: 'Email', type: 'text' },
    { key: 'phone', header: 'Phone', type: 'text' },
    { key: 'bio', header: 'Bio', type: 'text' },
    { key: 'headshot_url', header: 'Headshot URL', type: 'text' },
    { key: 'website', header: 'Website', type: 'text' },
    { key: 'instagram', header: 'Instagram', type: 'text' },
    { key: 'tiktok', header: 'TikTok', type: 'text' },
    { key: 'youtube', header: 'YouTube', type: 'text' }
  ]
};

/**
 * Export data to CSV
 * @param {string} entityType - Type of entity (musicals, songs, roles, talent_profiles)
 * @returns {Promise<void>}
 */
export async function exportToCSV(entityType) {
  try {
    // Validate entity type
    if (!COLUMN_DEFINITIONS?.[entityType]) {
      throw new Error(`Invalid entity type: ${entityType}`);
    }

    // Fetch data from Supabase
    const { data, error } = await supabase?.from(entityType)?.select('*')?.order('created_at', { ascending: false });

    if (error) throw error;

    if (!data || data?.length === 0) {
      throw new Error(`No ${entityType} found to export`);
    }

    // Convert to CSV
    const columns = COLUMN_DEFINITIONS?.[entityType];
    const csvString = convertToCSV(data, columns);

    // Download file
    const filename = `${entityType}_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    downloadCSV(csvString, filename);

    return {
      success: true,
      count: data?.length,
      filename
    };
  } catch (error) {
    console.error(`Export ${entityType} error:`, error);
    throw error;
  }
}

/**
 * Import data from CSV file
 * @param {string} entityType - Type of entity (musicals, songs, roles, talent_profiles)
 * @param {File} file - CSV file to import
 * @returns {Promise<object>} Import results
 */
export async function importFromCSV(entityType, file) {
  try {
    // Validate entity type
    if (!COLUMN_DEFINITIONS?.[entityType]) {
      throw new Error(`Invalid entity type: ${entityType}`);
    }

    // Read CSV file
    const csvString = await readCSVFile(file);

    // Parse CSV
    const columns = COLUMN_DEFINITIONS?.[entityType];
    const parsedData = parseCSV(csvString, columns);

    if (!parsedData || parsedData?.length === 0) {
      throw new Error('No valid data found in CSV file');
    }

    // Separate new records (no ID) and updates (with ID)
    const newRecords = parsedData?.filter(item => !item?.id);
    const updateRecords = parsedData?.filter(item => item?.id);

    let inserted = 0;
    let updated = 0;
    const errors = [];

    // Get current user for audit fields
    const { data: { user } } = await supabase?.auth?.getUser();
    const userId = user?.id;

    // Process new records (inserts)
    if (newRecords?.length > 0) {
      const recordsToInsert = newRecords?.map(record => {
        const { id, created_at, updated_at, ...rest } = record;
        return {
          ...rest,
          created_by: userId,
          updated_by: userId
        };
      });

      const { data: insertedData, error: insertError } = await supabase?.from(entityType)?.insert(recordsToInsert)?.select();

      if (insertError) {
        errors?.push({
          type: 'insert',
          message: insertError?.message,
          count: recordsToInsert?.length
        });
      } else {
        inserted = insertedData?.length || 0;
      }
    }

    // Process existing records (updates)
    for (const record of updateRecords) {
      const { id, created_at, created_by, ...rest } = record;
      
      const { error: updateError } = await supabase?.from(entityType)?.update({
          ...rest,
          updated_by: userId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id);

      if (updateError) {
        errors?.push({
          type: 'update',
          id,
          message: updateError?.message
        });
      } else {
        updated++;
      }
    }

    return {
      success: true,
      inserted,
      updated,
      total: parsedData?.length,
      errors: errors?.length > 0 ? errors : null
    };
  } catch (error) {
    console.error(`Import ${entityType} error:`, error);
    throw error;
  }
}

/**
 * Get column definitions for an entity type
 * @param {string} entityType - Type of entity
 * @returns {Array} Column definitions
 */
export function getColumnDefinitions(entityType) {
  return COLUMN_DEFINITIONS?.[entityType] || [];
}

/**
 * Validate CSV data before import
 * @param {string} entityType - Type of entity
 * @param {File} file - CSV file to validate
 * @returns {Promise<object>} Validation results
 */
export async function validateCSV(entityType, file) {
  try {
    // Validate entity type
    if (!COLUMN_DEFINITIONS?.[entityType]) {
      throw new Error(`Invalid entity type: ${entityType}`);
    }

    // Read and parse CSV
    const csvString = await readCSVFile(file);
    const columns = COLUMN_DEFINITIONS?.[entityType];
    const parsedData = parseCSV(csvString, columns);

    const errors = [];
    const warnings = [];

    // Validate each row
    parsedData?.forEach((row, index) => {
      const rowNumber = index + 2; // +2 for header and 0-index

      // Check required fields
      columns?.forEach(col => {
        if (col?.required && !row?.[col?.key]) {
          errors?.push({
            row: rowNumber,
            field: col?.header,
            message: `Missing required field: ${col?.header}`
          });
        }
      });

      // Check if ID exists for updates
      if (row?.id) {
        warnings?.push({
          row: rowNumber,
          message: 'Record will be updated (ID exists)'
        });
      } else {
        warnings?.push({
          row: rowNumber,
          message: 'New record will be created (no ID)'
        });
      }
    });

    return {
      valid: errors?.length === 0,
      totalRows: parsedData?.length,
      errors,
      warnings
    };
  } catch (error) {
    return {
      valid: false,
      errors: [{ message: error?.message }]
    };
  }
}